import { useContext, useState } from "react";
import { createFileRoute, Outlet, redirect, useLocation } from "@tanstack/react-router";

import { AddPlayerDialogV2 } from "~/common/components/add-player-dialog-v2.js";
import { InstallPWAButton } from "~/common/components/install-pwa-button.js";
import { IOSInstallPrompt } from "~/common/components/ios-install-prompt.js";
import { NotificationButton } from "~/common/components/enable-notifications-button.js";
import { SignOutButton } from "~/features/auth/_components/sign-out-button.js";
import { useVerificationNotifications } from "~/common/hooks/use-verification-notifications.js";
import { AuthContext } from "~/features/auth/_providers/auth.provider.js";
import { Tabs, TabsList, TabsTrigger } from "@acme/ui/tabs";

export const Route = createFileRoute("/_authed")({
  beforeLoad: async ({ context, location }) =>
  {
    if (!context.auth?.isAuthenticated)
    {
      throw redirect({
        to: "/sign-in",
        search: {
          redirect: location.href, // Full URL path
        },
      });
    }
  },
  component: RouteComponent,
});

function RouteComponent()
{
  const [showAddPlayerDialog, setShowAddPlayerDialog] = useState(false);
  const location = useLocation();
  const { auth: routerAuth } = Route.useRouteContext();
  const authFromReactContext = useContext(AuthContext);
  // Prefer router context (TanStack pattern); fallback to React AuthContext because
  // useRouteContext() can be undefined on first render in layout routes (TanStack Router #1618, #1637).
  const auth = routerAuth ?? authFromReactContext ?? undefined;
  const userId = auth?.isAuthenticated ? auth.user?.id : undefined;
  useVerificationNotifications(userId);

  const getActiveTab = () =>
  {
    if (location.pathname === "/my-players" || location.pathname.startsWith("/my-players/"))
    {
      return "players";
    }
    if (location.pathname === "/my-clans")
    {
      return "clans";
    }
    return "dashboard";
  };

  const navigate = Route.useNavigate();

  const handleTabChange = (value: string) =>
  {
    if (value === "dashboard")
    {
      void navigate({ to: "/" });
    }
    else if (value === "players")
    {
      void navigate({ to: "/my-players" });
    }
    else if (value === "clans")
    {
      void navigate({ to: "/my-clans" });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <IOSInstallPrompt />
      <header className="sticky top-0 z-10 border-b border-border bg-background dark:bg-[oklch(0.155_0_0)]">
        <div className="container mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 py-4 px-4">
          <Tabs value={getActiveTab()} onValueChange={handleTabChange} className="w-full sm:w-auto">
            <TabsList className="w-full sm:w-auto overflow-x-auto">
              <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
              <TabsTrigger value="players">My Players</TabsTrigger>
              <TabsTrigger value="clans">My Clans</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="w-full sm:w-auto flex items-center gap-2 justify-end">
            <InstallPWAButton />
            <NotificationButton />
            <SignOutButton />
          </div>
        </div>
      </header>
      <AddPlayerDialogV2 open={showAddPlayerDialog} onOpenChange={setShowAddPlayerDialog} />
      <Outlet />
    </div>
  );
}
